import { FC, useMemo } from 'react'

import { F0AvatarList, F0Box, F0Card, F0Text } from '@factorialco/f0-react'
import { Suitcase } from '@factorialco/f0-react/icons/app'
import { GoalAssigneeEmployeeFragment } from 'generated/resources/graphql'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

interface AssigneeCardProps {
  assignees: GoalAssigneeEmployeeFragment[]
  totalCount: number
}

export const AssigneeCard: FC<AssigneeCardProps> = ({ assignees, totalCount }) => {
  const singleAssignee = totalCount === 1 ? assignees[0] : undefined

  if (singleAssignee) {
    return <SingleAssigneeCard assignee={singleAssignee} />
  }

  return <MultipleAssigneesCard assignees={assignees} totalCount={totalCount} />
}

const SingleAssigneeCard: FC<{ assignee: GoalAssigneeEmployeeFragment }> = ({ assignee }) => {
  const { navigation } = useNavigation()

  const firstName = assignee.employee?.firstName ?? ''
  const lastName = assignee.employee?.lastName ?? ''

  const link = navigation.goals.employees.employee.fullPath({ id: String(assignee.id) })

  const avatar = { type: 'person' as const, firstName, lastName }

  const jobTitle = assignee.employee?.referenceContract?.jobTitle
  const metadata = jobTitle ? [
    {
      icon: Suitcase,
      property: {
        type: 'text' as const,
        value: jobTitle,
      },
    },
  ] : undefined

  return (
    <F0Card
      title={assignee.fullName ?? ''}
      link={link}
      avatar={avatar}
      description={i18n.t('goals_revamp.detail.assigned_to')}
      metadata={metadata}
    />
  )
}

const MultipleAssigneesCard: FC<{ assignees: GoalAssigneeEmployeeFragment[]; totalCount: number }> = ({ assignees, totalCount }) => {
  const avatars = useMemo(
    () => assignees.map((a) => ({
      firstName: a.employee?.firstName ?? '',
      lastName: a.employee?.lastName ?? '',
    })),
    [assignees]
  )

  const remainingCount = totalCount > assignees.length ? totalCount - assignees.length : undefined

  return (
    <F0Card
      title={i18n.t('goals_revamp.detail.assigned_to')}
    >
      <F0Box display="grid" columns="5" alignItems="center" paddingX="md" paddingBottom="md">
        <F0Box colSpan="1">
          <F0Text variant="label" content={totalCount.toString()} />
        </F0Box>

        <F0Box colSpan="4" minWidth="0" overflow="hidden">
          <F0AvatarList
            type="person"
            avatars={avatars}
            size="sm"
            remainingCount={remainingCount}
            layout="fill"
          />
        </F0Box>
      </F0Box>
    </F0Card>
  )
}
